import {
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import axios from "axios";
import React, { useState } from "react";
import { FaEllipsisV, FaFolder, FaPencilAlt, FaTrash } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteFolder,
  fetchFolders,
  moveToFolder,
  updateFolderName,
} from "../../_actions/folderActions";
import { TldrConfirmationModal } from "../common/statelessView";
import { lightInactive } from "../styled/variable";
import {
  folderComponentStyles,
  TldrNewFolderDialog,
} from "../styled/home/statelessView";
import TldrMoveToFolderModal from "./TldrMoveToFolderModal";
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';

const FolderCard = (props) => {
  const { t } = useTranslation(["common"]);
  const signal = axios.CancelToken.source();

  const [inProgress, setInProgress] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [showMoveToFolder, setShowMoveToFolder] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);

  const folders = useSelector((state) => state.folders.folders);
  const hasMore = useSelector((state) => state.folders.hasMore);
  const foldersPage = useSelector((state) => state.folders.foldersPage);
  const recentFolders = useSelector((state) => state.folders.recent);
  const dispatch = useDispatch();

  const { folder, maxWidth } = props;
  const { name, id } = folder;

  function handleOnClick() {
    props.onClick();
  }

  function openRename() {
    setShowRename(true);
  }

  function handleRename(newName) {
    const payload = {
      name: newName,
    };

    dispatch(updateFolderName(id, payload, signal.token))
      .then((res) => {
        setInProgress(false);
        setShowRename(false);
      })
      .catch((error) => {
        setInProgress(false);
        setShowRename(false);
      });
  }

  function openConfirmation() {
    setShowConfirmation(true);
  }

  function handleDelete(folderToDelete) {
    dispatch(deleteFolder(folderToDelete, signal.token, props))
      .then((res) => {
        setInProgress(false);
        setShowConfirmation(false);
      })
      .catch((error) => {
        setInProgress(false);
        setShowConfirmation(false);
      });
  }

  function handleMoveToFolder(folderId, folderName) {
    const payload = {
      content_ids: [id],
    };

    dispatch(moveToFolder(payload, folderId, folderName, signal.token)).then(
      () => {
        setInProgress(false);
        setShowMoveToFolder(false);
        getFolders();
      }
    );
  }

  function createNewFolder(name) {
    dispatch(createNewFolder(name, signal.token, props))
      .then((folder) => {
        setInProgress(false);
        setShowNewFolder(false);
        handleMoveToFolder(folder.id, folder.name);
      })
      .catch(() => {
        setInProgress(false);
        setShowNewFolder(false);
      });
  }

  function getFolders() {
    const payload = {
      searchTerm: "",
      props: {
        folders,
        hasMore,
        foldersPage,
        recentFolders,
      },
      updateRecent: true,
    };

    dispatch(fetchFolders(payload, signal.token));
  }

  return (
    <>
      <Flex
        sx={folderComponentStyles}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={handleOnClick}
      >
        <Flex
          direction="row"
          alignItems="center"
          maxW={maxWidth}
        >
          <Icon as={FaFolder} color={lightInactive} mr={3} />
          <Text isTruncated>{name}</Text>
        </Flex>

        <div onClick={(e) => e.stopPropagation()}>
          <Menu isLazy>
            <MenuButton
              as={IconButton}
              icon={<Icon as={FaEllipsisV} />}
              variant="unstyled"
              sx={{
                ":focus": {
                  boxShadow: "none",
                },
              }}
            />
            <MenuList>
              <MenuItem icon={<Icon as={FaPencilAlt} />} onClick={openRename}>
                {t("rename")}
              </MenuItem>
              <MenuItem icon={<Icon as={FaTrash} />} onClick={openConfirmation}>
                {t("delete")}
              </MenuItem>

              {/* @TODO: back-end does not support nested folders. 
              Uncomment below code once back-end is ready */}
              {/* <MenuItem
                icon={<Icon as={FaArrowAltCircleUp} />}
                onClick={openMoveToFolder}
              >
                Move to folder
              </MenuItem> */}
            </MenuList>
          </Menu>
        </div>
      </Flex>

      {showRename ? (
        <div onClick={(e) => e.stopPropagation()}>
          <TldrNewFolderDialog
            inProgress={inProgress}
            show={showRename}
            value={folder ? name : ""}
            rename
            onHide={() => {
              setShowRename(false);
            }}
            onYes={(name) => {
              setInProgress(true);
              handleRename(name);
            }}
          />
        </div>
      ) : null}

      {showConfirmation ? (
        <div onClick={(e) => e.stopPropagation()}>
          <TldrConfirmationModal
            modalFor="folder"
            inProgress={inProgress}
            show={showConfirmation}
            onHide={() => {
              setShowConfirmation(false);
            }}
            onYes={() => {
              setInProgress(true);
              handleDelete(folder);
            }}
          />
        </div>
      ) : null}

      {showMoveToFolder ? (
        <div onClick={(e) => e.stopPropagation()}>
          <TldrMoveToFolderModal
            inProgress={inProgress}
            show={showMoveToFolder}
            onHide={(e) => {
              setShowMoveToFolder(false);
            }}
            onYes={(folderId, folderName) => {
              setInProgress(true);
              handleMoveToFolder(folderId, folderName);
            }}
            onAddFolder={() => {
              setShowNewFolder(true);
              setShowMoveToFolder(false);
            }}
            currentFolderId={id}
          />
        </div>
      ) : null}

      {showNewFolder ? (
        <div onClick={(e) => e.stopPropagation()}>
          <TldrNewFolderDialog
            inProgress={inProgress}
            show={showNewFolder}
            onHide={() => {
              setShowNewFolder(false);
            }}
            onYes={(name) => {
              setInProgress(true);
              createNewFolder(name);
            }}
          />
        </div>
      ) : null}
    </>
  );
};

FolderCard.propTypes = {
  onClick: PropTypes.func.isRequired,
};

FolderCard.defaultProps = {
  onClick: () => { },
};

export default FolderCard;
